import {
  COUNTRIES_DATA,
  i18nService,
  mobileNumberDigitsLimits,
  CountryObject,
} from '@zonofi/i18n';
import { parsePhoneNumberFromString } from 'libphonenumber-js/mobile';

export const appendCountryCode = (mobile: string): string => {
  if (mobile.length === 0) return '';
  const countryCode = i18nService.getCurrentDialCode();
  return `${countryCode}${mobile}`;
};

interface PhoneNumberMetadata {
  phone: string;
  dialCode?: string;
  countryCode?: string;
}

type CountryLike = Partial<CountryObject>;

export const splitPhoneNumber = (mobile: string): PhoneNumberMetadata => {
  if (!mobile || mobile.trim().length === 0) return { phone: '' };
  const cleaned = mobile.trim().replace(/^0+/, '');
  const phone = cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
  const phoneNumber = parsePhoneNumberFromString(phone);

  if (phoneNumber) {
    return {
      countryCode: phoneNumber.country,
      dialCode: `+${phoneNumber.countryCallingCode}`,
      phone: phoneNumber.nationalNumber,
    };
  }
  return { phone: phone.slice(1) };
};

export const isPhoneNumberValid = (
  mobileNumber: string,
  countryCode: string
) => {
  const digits = mobileNumber.replace(/\D/g, '');

  return (
    digits.trim().length >= mobileNumberDigitsLimits[countryCode][0] &&
    digits.trim().length <= mobileNumberDigitsLimits[countryCode][1]
  );
};

export const getPhoneMaxLength = (fallback = 15): number => {
  const countryCode = i18nService.getCurrentCountryCode()?.toUpperCase();
  return mobileNumberDigitsLimits[countryCode][1] ?? fallback;
};

export const getPhoneMinLength = (fallback = 7): number => {
  const countryCode = i18nService.getCurrentCountryCode()?.toUpperCase();
  return mobileNumberDigitsLimits[countryCode][0] ?? fallback;
};

const getCountryByDialCode = (dialCode?: string): CountryLike | undefined => {
  if (!dialCode) return undefined;
  const normalizedDialCode = dialCode.startsWith('+')
    ? dialCode
    : `+${dialCode}`;
  return COUNTRIES_DATA.find((x) => x.dial_code === normalizedDialCode);
};

const getResolvedCountry = (
  phoneValue?: string,
  fallbackCountry?: CountryLike
): CountryLike | undefined => {
  const parsedDialCode = splitPhoneNumber(String(phoneValue ?? '')).dialCode;
  const countryFromPhone = getCountryByDialCode(parsedDialCode);
  if (countryFromPhone) {
    return countryFromPhone;
  }
  if (fallbackCountry?.code) {
    return fallbackCountry;
  }
  return getCountryByDialCode(fallbackCountry?.dial_code);
};

export const getExpectedPhoneLengthForCountry = (
  country?: CountryLike,
  phoneValue?: string
): number => {
  const resolvedCountry = getResolvedCountry(phoneValue, country);

  const expectedLengthFromCountry = resolvedCountry?.code
    ? mobileNumberDigitsLimits[resolvedCountry.code.toUpperCase()]?.[1]
    : undefined;

  if (expectedLengthFromCountry) {
    return expectedLengthFromCountry;
  }

  // Backward-compatible fallback to global i18nService behavior
  const previousCountryCode = i18nService.getCurrentCountryCode();
  const previousDialCode = i18nService.getCurrentDialCode();

  if (resolvedCountry?.code) {
    i18nService.setCurrentCountryCode(resolvedCountry.code);
  }
  if (resolvedCountry?.dial_code) {
    i18nService.setCurrentDialCode(resolvedCountry.dial_code);
  }

  const expectedLength = getPhoneMaxLength();

  if (previousCountryCode) {
    i18nService.setCurrentCountryCode(previousCountryCode);
  }
  if (previousDialCode) {
    i18nService.setCurrentDialCode(previousDialCode);
  }

  return expectedLength;
};

export const formatPhoneWithResolvedCountry = (
  phoneValue?: string,
  fallbackCountry?: CountryLike
): string => {
  if (!phoneValue) {
    return '';
  }

  const parsed = splitPhoneNumber(phoneValue);
  if (parsed.dialCode && parsed.phone) {
    return `${parsed.dialCode.replace(/^\+/, '')}${parsed.phone}`;
  }

  const digitsOnly = String(phoneValue).replace(/\D/g, '');
  const resolvedCountry = getResolvedCountry(phoneValue, fallbackCountry);
  const dialCode = resolvedCountry?.dial_code?.replace(/^\+/, '');

  if (!dialCode) {
    return digitsOnly;
  }

  return digitsOnly.startsWith(dialCode)
    ? digitsOnly
    : `${dialCode}${digitsOnly}`;
};
